/**
 * Cenovnik modula.
 *
 * Do sada nijedna ruta nije pisala u `module_prices` i nije bilo ekrana - cene su
 * se menjale isključivo `UPDATE`-om iz konzole. Promena cene je odluka i mora da
 * ima autora, datum i trag.
 *
 * Cenovnik je **knjiga**: nova cena je nov red sa datumom od kog važi, ništa se
 * ne prepisuje. Zato ekran pokazuje tri stvari odvojeno - šta važi danas, šta je
 * najavljeno za ubuduće, i cela istorija.
 */
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Megaphone, RefreshCw, Tag, X } from 'lucide-react';
import { api } from '../../api/client';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';
import { Uputstvo, Pojam } from '../../components/Uputstvo';

type Cena = {
  moduleCode: string;
  monthlyPriceRsd: string;
  yearlyPriceRsd: string | null;
  extraSeatPriceRsd: string | null;
  validFrom: string;
  createdBy: string | null;
};

type Red = {
  moduleCode: string;
  name: string;
  isAddon: boolean;
  current: Cena | null;
  upcoming: Cena[];
  history: Cena[];
};

const dinar = (v: string | null | undefined) =>
  v == null ? '-' : `${Number(v).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD`;

const datum = (iso: string) => new Date(iso).toLocaleDateString('sr-RS');

export function ModulePricesPage() {
  const [redovi, setRedovi] = useState<Red[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [otvoren, setOtvoren] = useState<string | null>(null);
  const [promena, setPromena] = useState<Red | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRedovi(await api.get<Red[]>('/admin/module-prices'));
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const najavi = async (moduleCode: string) => {
    setError(null); setPoruka(null);
    try {
      const r = await api.post<{ notified: number; validFrom: string }>('/admin/module-prices/announce', { moduleCode });
      setPoruka(`Najava poslata: ${r.notified} klijent(a), nova cena od ${r.validFrom}.`);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Najava nije poslata');
    }
  };

  const bezCene = redovi.filter((r) => !r.current).length;

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Cenovnik</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Nova cena je <strong>nov red</strong>, ne izmena - stara ostaje da bi se znalo po čemu je račun izdat
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => void refresh()} disabled={loading} style={btnSecondary}>
          <RefreshCw size={13} /> Osveži
        </button>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {poruka && <div style={okBox}><Check size={14} /> {poruka}</div>}

      <Uputstvo id="cenovnik" naslov="Kako radi cenovnik - pročitaj pre prve izmene cene">
        <Pojam naziv="Knjiga, ne izmena">
          Nova cena je <strong>nov red</strong> sa datumom od kog važi; stara ostaje. Zato se cena nikad
          ne „prepravlja" - inače se posle ne bi znalo po čemu je koji račun izdat.
        </Pojam>
        <Pojam naziv="Mesečno / Godišnje">
          Ako godišnja nije upisana, uzima se <strong>deset mesečnih</strong> (dva meseca gratis).
        </Pojam>
        <Pojam naziv="Mesto">
          Cena svakog <strong>uređaja preko prvog</strong>. Licenca sa 3 mesta = osnovna cena + 2 × ovaj iznos.
          Ako iznos nije upisan, doplate nema - ne pretpostavlja se nikakva cena.
        </Pojam>
        <Pojam naziv="Važi od">
          Bez datuma - od danas. Datum u budućnosti je <strong>najava poskupljenja</strong>: zatečene
          pretplate do tog dana plaćaju po svojoj ceni. Poskupljenje se najavljuje najmanje 30 dana ranije,
          pa se posle upisa pošalje najava dugmetom <em>najavi</em>.
        </Pojam>
        <Pojam naziv="Modul bez cene">
          Ne nudi se klijentu u katalogu. Modul bez cene se ne sme prodati po nuli, pa se radije ne prikazuje.
        </Pojam>

        <div style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--color-bg)', lineHeight: 1.6 }}>
          <strong>Zatečena pretplata ne poskupljuje sama.</strong> Cena po kojoj je klijent kupio snima se
          na njegovu licencu i cenovnik je ne dodiruje <strong>do obnove</strong>. Izmena ovde važi za nove
          kupovine i za obnove posle datuma važenja.
        </div>

        <div style={{ padding: '8px 10px', borderRadius: 6, background: 'var(--color-bg)', lineHeight: 1.6 }}>
          <strong>Predračuni i avansni računi nisu u ovom spisku.</strong> To su vrste fiskalnog računa koje
          po propisu izdaje svako ko ima kasu, pa idu uz <code>POS_BASIC</code> - ne prodaju se i ne
          naplaćuju. Cena im se ne može upisati ni preko rute.
        </div>
      </Uputstvo>

      {bezCene > 0 && (
        <div style={{ ...warnBox }}>
          <AlertTriangle size={14} />
          <span>
            {bezCene} {bezCene === 1 ? 'modul nema cenu' : 'modula nemaju cenu'} - takvi se
            <strong> ne nude klijentu u katalogu</strong>, jer se modul bez cene ne sme prodati po nuli.
          </span>
        </div>
      )}

      {/*
        Tabela ima NAJMANJU SIRINU i vodoravno klizanje.

        Ranije je bila `tableLayout: fixed` sa gomilom fiksnih kolona i kolonom
        „Modul" bez ogranicenja - na uzem ekranu se naziv prelamao, a sifra modula
        (monospace, bez preloma) izlazila iz celije i **preklapala oznaku tipa**.
        Bolje je da se tabela klizi nego da se tekst preklapa.
      */}
      <div style={{ ...card, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 860, borderCollapse: 'collapse', tableLayout: 'fixed' }} data-testid="prices-table">
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ ...th, width: 40 }} />
              <th style={{ ...th, minWidth: 240 }}>Modul</th>
              <th style={{ ...th, width: 130, textAlign: 'right' }}>Mesečno</th>
              <th style={{ ...th, width: 130, textAlign: 'right' }}>Godišnje</th>
              <th style={{ ...th, width: 100, textAlign: 'right' }}>Mesto</th>
              <th style={{ ...th, width: 170 }}>Najavljeno</th>
              <th style={{ ...th, ...lepljiva, width: 120, textAlign: 'right', background: 'var(--color-bg)' }} />
            </tr>
          </thead>
          <tbody>
            {redovi.map((r) => {
              const open = otvoren === r.moduleCode;
              return (
                <Fragment key={r.moduleCode}>
                  <tr data-testid={`cena-${r.moduleCode}`}>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button
                        onClick={() => setOtvoren(open ? null : r.moduleCode)}
                        title={open ? 'Sakrij istoriju' : 'Prikaži istoriju'}
                        style={iconBtn}
                      >
                        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td style={{ ...td, overflow: 'hidden' }}>
                      {/* Oznaka tipa je UZ naziv, ne u svojoj koloni - tako ne moze
                          da se sudari sa sifrom kad se sirina smanji. */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{
                          fontWeight: 600, color: 'var(--color-text-strong)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>{r.name}</span>
                        <span style={{ ...oznaka(r.isAddon), flexShrink: 0 }}>{r.isAddon ? 'dodatak' : 'osnovni'}</span>
                      </div>
                      <div style={{
                        fontSize: 11, color: 'var(--color-text-muted)', fontFamily: 'monospace',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{r.moduleCode}</div>
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }} className="tabular-nums">
                      {r.current ? dinar(r.current.monthlyPriceRsd) : <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>nema cenu</span>}
                    </td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} className="tabular-nums">{dinar(r.current?.yearlyPriceRsd)}</td>
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }} className="tabular-nums">{dinar(r.current?.extraSeatPriceRsd)}</td>
                    <td style={{ ...td, overflow: 'hidden' }}>
                      {r.upcoming.length === 0 ? (
                        <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>-</span>
                      ) : (
                        <div style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          <div>{dinar(r.upcoming[0].monthlyPriceRsd)}</div>
                          <div style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
                            od {datum(r.upcoming[0].validFrom)}
                            <button onClick={() => void najavi(r.moduleCode)} style={linkBtn} data-testid={`najavi-${r.moduleCode}`}>
                              <Megaphone size={10} /> najavi
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                    {/* Glavna radnja je zalepljena uz desnu ivicu: kad tabela
                        klizi, dugme ostaje na ekranu umesto da se odsece. */}
                    <td style={{ ...td, ...lepljiva, textAlign: 'right', background: 'var(--color-bg-surface)' }}>
                      <button onClick={() => setPromena(r)} style={btnSecondary} data-testid={`promeni-${r.moduleCode}`}>
                        {r.current ? 'Promeni' : 'Postavi'}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr key={`${r.moduleCode}-h`}>
                      <td colSpan={7} style={{ ...td, background: 'var(--color-bg)' }}>
                        <Istorija redovi={r.history} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {promena && (
        <PromenaCene
          red={promena}
          onZatvori={() => setPromena(null)}
          onSacuvano={(p) => { setPromena(null); setPoruka(p); void refresh(); }}
          onGreska={setError}
        />
      )}
    </div>
  );
}

function Istorija({ redovi }: { redovi: Cena[] }) {
  if (redovi.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Za ovaj modul još nije upisana nijedna cena.</div>;
  }
  const sada = Date.now();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Istorija cena
      </div>
      {redovi.map((c) => {
        const buduca = new Date(c.validFrom).getTime() > sada;
        return (
          <div key={c.validFrom} style={{ display: 'flex', gap: 16, fontSize: 12, color: buduca ? 'var(--color-text-muted)' : 'var(--color-text)' }}>
            <span style={{ minWidth: 100 }}>od {datum(c.validFrom)}</span>
            <span className="tabular-nums" style={{ minWidth: 120 }}>{dinar(c.monthlyPriceRsd)} / mes.</span>
            <span className="tabular-nums" style={{ minWidth: 130 }}>{dinar(c.yearlyPriceRsd)} / god.</span>
            {buduca && <span style={{ fontStyle: 'italic' }}>još ne važi</span>}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Promena cene - dijalog koji se otvara **sa reda**.
 *
 * Ranije je jedini nacin bio obrazac na dnu strane, sa padajucom listom modula:
 * trebalo je otici do dna, pa naci modul u listi. Sada se cena menja tamo gde se
 * i vidi, a polja su popunjena zatecenim vrednostima da se prekucava samo ono sto
 * se zaista menja.
 */
function PromenaCene({ red, onZatvori, onSacuvano, onGreska }: {
  red: Red;
  onZatvori: () => void;
  onSacuvano: (poruka: string) => void;
  onGreska: (e: string) => void;
}) {
  useEscapeToClose(onZatvori);
  const [mesecno, setMesecno] = useState(red.current?.monthlyPriceRsd ?? '');
  const [godisnje, setGodisnje] = useState(red.current?.yearlyPriceRsd ?? '');
  const [mesto, setMesto] = useState(red.current?.extraSeatPriceRsd ?? '120.00');
  const [odKad, setOdKad] = useState('');
  const [radi, setRadi] = useState(false);

  const staraMesecna = red.current ? Number(red.current.monthlyPriceRsd) : null;
  const nova = Number(mesecno);
  const poskupljenje = staraMesecna != null && Number.isFinite(nova) && nova > staraMesecna;

  const sacuvaj = async () => {
    setRadi(true);
    try {
      const r = await api.post<{ validFrom: string; noticeWarning: string | null }>('/admin/module-prices', {
        moduleCode: red.moduleCode,
        monthlyPriceRsd: mesecno,
        yearlyPriceRsd: godisnje || null,
        extraSeatPriceRsd: mesto || null,
        validFrom: odKad || undefined,
      });
      onSacuvano(r.noticeWarning ?? `Cena za ${red.name} važi od ${r.validFrom}.`);
    } catch (e: any) {
      onGreska(e?.data?.message || e?.message || 'Cena nije upisana');
    } finally {
      setRadi(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {red.current ? 'Promena cene' : 'Postavljanje cene'} - {red.name}
          </h3>
          <button onClick={onZatvori} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </header>

        <div style={{ padding: 20 }}>
          {red.current && (
            <div style={{ ...napomena, marginBottom: 14 }}>
              Trenutno: <strong>{dinar(red.current.monthlyPriceRsd)}</strong> mesečno, važi od {datum(red.current.validFrom)}.
              Stara cena <strong>ostaje u knjizi</strong> - upisuje se nov red.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Polje label="Mesečno (RSD)">
              <input value={mesecno} onChange={(e) => setMesecno(e.target.value)} placeholder="3000.00" style={input} data-testid="cena-mesecno" autoFocus />
            </Polje>
            <Polje label="Godišnje (RSD)">
              <input value={godisnje} onChange={(e) => setGodisnje(e.target.value)} placeholder="bez unosa = 10× mesečno" style={input} />
            </Polje>
            <Polje label="Dodatno mesto (RSD)">
              <input value={mesto} onChange={(e) => setMesto(e.target.value)} style={input} />
            </Polje>
            <Polje label="Važi od">
              <input type="date" value={odKad} onChange={(e) => setOdKad(e.target.value)} style={input} data-testid="cena-vazi-od" />
            </Polje>
          </div>

          <div style={napomena}>
            Bez datuma cena važi <strong>od danas</strong>. Datum u budućnosti je najava poskupljenja -
            zatečene pretplate do tada plaćaju po svojoj ceni.
          </div>

          {poskupljenje && !odKad && (
            <div style={{ ...napomena, marginTop: 8, background: 'rgba(245,158,11,0.12)', color: '#fcd34d' }}>
              <AlertTriangle size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
              Ovo je <strong>poskupljenje bez najave</strong>. Poskupljenje se najavljuje najmanje 30 dana ranije -
              stavite datum u budućnost, pa pošaljite najavu sa spiska.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button onClick={onZatvori} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button
              onClick={() => void sacuvaj()}
              disabled={radi || !mesecno.trim()}
              style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: radi || !mesecno.trim() ? 0.5 : 1 }}
              data-testid="cena-sacuvaj"
            >
              <Tag size={13} /> {radi ? 'Čuvanje…' : 'Sačuvaj'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Polje({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>
      {children}
    </div>
  );
}

const oznaka = (dodatak: boolean): React.CSSProperties => ({
  fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 600, textTransform: 'uppercase',
  background: dodatak ? 'var(--color-bg)' : 'rgba(59,130,246,0.15)',
  color: dodatak ? 'var(--color-text-muted)' : '#93c5fd',
});

const card: React.CSSProperties = { background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' };
const th: React.CSSProperties = { padding: '12px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border)' };
const btnSecondary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, background: 'var(--color-primary, #2563eb)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 4, borderRadius: 4, cursor: 'pointer' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 12, outline: 'none' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 14 };
const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(34,197,94,0.15)', color: '#86efac', fontSize: 13, marginBottom: 14 };
const warnBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(245,158,11,0.15)', color: '#fcd34d', fontSize: 13, marginBottom: 14 };
const linkBtn: React.CSSProperties = { marginLeft: 8, background: 'transparent', border: 'none', color: 'var(--color-primary, #60a5fa)', cursor: 'pointer', padding: 0, fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 3 };
const napomena: React.CSSProperties = { padding: 10, borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text-muted)', fontSize: 12, lineHeight: 1.5 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 520, background: 'var(--color-bg-surface)', borderRadius: 12, border: '1px solid var(--color-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' };
/** Kolona koja ostaje uz desnu ivicu dok tabela klizi vodoravno. */
const lepljiva: React.CSSProperties = {
  position: 'sticky',
  right: 0,
  // Bez senke se ne vidi da sadrzaj klizi ISPOD kolone, pa izgleda kao da je odsecen.
  boxShadow: '-8px 0 8px -8px rgba(0,0,0,0.5)',
};
